import { useCallback, useEffect, useState } from 'react';
import { PageHeader } from '@/components/shared/PageHeader';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { RefreshCw } from 'lucide-react';

interface AppRatingRow {
  id: string;
  user_id: string;
  username: string | null;
  rating: number;
  comment: string | null;
  created_at: string;
}

function parseRows(data: unknown): AppRatingRow[] {
  if (data && typeof data === 'string') {
    try {
      return parseRows(JSON.parse(data) as unknown);
    } catch {
      return [];
    }
  }
  return normalizeRpcResponse(data as AppRatingRow | null | undefined) as AppRatingRow[];
}

export function AppRatingFeedbackPage() {
  const [rows, setRows] = useState<AppRatingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    const { data, error } = await dbClient.rpc<unknown>('admin_list_app_rating_feedback', { p_limit: 500 });
    if (error) {
      setErr(error.message);
      setRows([]);
    } else {
      setRows(parseRows(data));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Valoraciones de la app"
        description="Comentarios enviados desde la misión «Valora la App» cuando el usuario elige menos de 5 estrellas."
        className="mb-0"
      >
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm font-medium hover:bg-muted/60 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </button>
      </PageHeader>

      {err ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">{err}</div>
      ) : null}

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Fecha</th>
                <th className="px-4 py-3">Usuario</th>
                <th className="px-4 py-3">Estrellas</th>
                <th className="px-4 py-3">Comentario</th>
                <th className="px-4 py-3">User ID</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                    Cargando…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                    No hay entradas todavía.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.id} className="border-t border-border hover:bg-muted/30">
                    <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                      {r.created_at
                        ? format(new Date(r.created_at), "d MMM yyyy HH:mm", { locale: es })
                        : '—'}
                    </td>
                    <td className="px-4 py-3 font-medium">{r.username || '—'}</td>
                    <td className="px-4 py-3">{r.rating}</td>
                    <td className="px-4 py-3 max-w-md break-words">{r.comment || '—'}</td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{r.user_id}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
